import React, { useContext, useMemo, useRef } from "react";
import { Form } from "react-final-form";
import { IntlContext, ListContext } from "context";
import { listContextDefaultChangeFetcherParams } from "context/List/Context";
import arrayMutators from "final-form-arrays";
import useQueryString from "helpers/useQueryString";
import { Button } from "components/ui/button";

type Pager = { per_page: number; order?: string; direction?: string };

export type FilterFormProps = {
    onSubmitSuccess?: () => void;
    defaultFilters?: HashMap<unknown>;
    persistentParams?: HashMap<unknown>;
    title?: string;
    withButtonSubmit?: boolean;
    initialFilters?: HashMap<unknown>;
    pager?: Pager;
    onChangeFetcherParams?: (params: HashMap<unknown>) => void;
    actions?: React.ReactNode;
    children: React.ReactNode;
};

const buildResetValues = (
    registeredFields: string[],
    defaultFilters: HashMap<unknown>,
): HashMap<unknown> =>
    registeredFields.reduce<HashMap<unknown>>((acc, fieldName) => {
        acc[fieldName] = fieldName in defaultFilters ? defaultFilters[fieldName] : "";
        return acc;
    }, {});

const pickRegisteredFilterValues = (values: HashMap<unknown>, registeredFields: string[]): HashMap<unknown> =>
    registeredFields.reduce<HashMap<unknown>>((acc, fieldName) => {
        const value = values[fieldName];
        if (value !== undefined && value !== "") {
            acc[fieldName] = value;
        }
        return acc;
    }, {});

const FilterForm: React.FC<FilterFormProps> = ({
    onSubmitSuccess,
    defaultFilters = {},
    persistentParams = {},
    title,
    withButtonSubmit = true,
    initialFilters,
    pager: pagerProp,
    onChangeFetcherParams,
    actions,
    children,
}) => {
    const intl = useContext(IntlContext);
    const listContext = useContext(ListContext);
    const queryString = useQueryString();
    const warnedOutsideListProviderRef = useRef(false);
    const registeredFieldsRef = useRef<string[]>([]);

    const activeFilters = initialFilters ?? (listContext.data.active_filters as HashMap<unknown>);
    const formInitialValues = useMemo(() => {
        const values: HashMap<unknown> = { ...defaultFilters, ...activeFilters };
        for (const fieldName of registeredFieldsRef.current) {
            if (!(fieldName in values)) {
                values[fieldName] = "";
            }
        }
        return values;
    }, [defaultFilters, activeFilters]);
    const pager: Pager = pagerProp ?? listContext.data.pager;
    const changeFetcherParams = onChangeFetcherParams ?? listContext.changeFetcherParams;

    if (
        onChangeFetcherParams === undefined &&
        listContext.changeFetcherParams === listContextDefaultChangeFetcherParams &&
        !warnedOutsideListProviderRef.current
    ) {
        warnedOutsideListProviderRef.current = true;
        console.warn(
            "[FilterForm] Relying on ListContext.changeFetcherParams, but no ListProvider is active (default no-op). " +
                "Wrap the tree with ListProvider or pass onChangeFetcherParams (and pager/initialFilters if needed).",
        );
    }

    return (
        <Form
            initialValues={formInitialValues}
            mutators={{ ...arrayMutators }}
            onSubmit={(values, form) => {
                const filterValues = pickRegisteredFilterValues(values, form.getRegisteredFields());
                const params = Object.assign({}, persistentParams, filterValues, {
                    per_page: pager.per_page,
                    page: 1,
                    order: pager.order,
                    direction: pager.direction,
                });
                changeFetcherParams(params);
                Object.keys(filterValues).length
                    ? queryString.setAll(filterValues)
                    : queryString.reset();
                onSubmitSuccess && onSubmitSuccess();
            }}
            render={({ handleSubmit, submitting, form }) => {
                registeredFieldsRef.current = form.getRegisteredFields();

                return (
                <form onSubmit={handleSubmit}>
                    {title && <p className="h3 margin-bottom--2">{title}</p>}
                    <div className="filters__group">
                        {children}

                        {(actions || withButtonSubmit) && (
                            <div className="filters__actions">
                                {actions}
                                {withButtonSubmit && (
                                    <div className="filters__btn-group">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            disabled={submitting}
                                            onClick={() => {
                                                const registeredFields = form.getRegisteredFields();
                                                const resetValues = buildResetValues(registeredFields, defaultFilters);
                                                const filterValues = pickRegisteredFilterValues(
                                                    resetValues,
                                                    registeredFields,
                                                );
                                                form.restart(resetValues);
                                                Object.keys(filterValues).length
                                                    ? queryString.setAll(filterValues)
                                                    : queryString.reset();
                                                changeFetcherParams(
                                                    Object.assign({}, persistentParams, filterValues, {
                                                        per_page: pager.per_page,
                                                        page: 1,
                                                        order: pager.order,
                                                        direction: pager.direction,
                                                    }),
                                                );
                                            }}
                                        >
                                            {intl.formatMessage("generic.crud.filter.reset.init")}
                                        </Button>
                                        <Button type="submit" disabled={submitting} className="rounded-xl">
                                            {intl.formatMessage("generic.crud.filter.submit")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </form>
                );
            }}
        />
    );
};

export default FilterForm;
